import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import campaignParticipantService from '../../services/campaignParticipantService';
import { doc, getDoc, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';

import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '../../components/ui/select';

// Icons
import {
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  ArrowRight,
  FileText,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';

const Applications = () => {
  const { currentUser } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: '',
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    const fetchApplications = async () => {
      if (!currentUser) return;

      try {
        setLoading(true);
        setError(null);
        
        // Fetch applications from Firestore
        const options = {
          orderBy: 'appliedAt',
          orderDirection: 'desc'
        };
        
        // Fetch all applications (we'll filter client-side for better UX)
        const applicationsData = await campaignParticipantService.getCampaignsByInfluencerId(currentUser.uid, options);

        // Enhance applications with draft and content review status
        const enhancedApplications = await Promise.all(applicationsData.map(async (application) => {
          // Check for draft status if approved
          if (application.status === 'approved') {
            try {
              // Fetch draft status from contentDrafts collection
              const draftsRef = collection(db, 'contentDrafts');
              const draftQuery = query(
                draftsRef,
                where('campaignId', '==', application.campaignId),
                where('influencerId', '==', currentUser.uid),
                orderBy('submittedAt', 'desc')
              );
              
              const draftSnapshot = await getDocs(draftQuery);
              if (!draftSnapshot.empty) {
                const latestDraft = draftSnapshot.docs[0].data();
                application.draftStatus = latestDraft.status || 'not_submitted';
                application.draftId = draftSnapshot.docs[0].id;
                application.draftFeedback = latestDraft.brandFeedback || '';
                application.draftSubmittedAt = latestDraft.submittedAt;
              } else {
                application.draftStatus = 'not_submitted';
              }
            } catch (err) {
              application.draftStatus = 'not_submitted';
            }
            
            // Fetch proof status if exists
            if (application.proofId) {
              try {
                const proofDoc = await getDoc(doc(db, 'proofs', application.proofId));
                if (proofDoc.exists()) {
                  const proofData = proofDoc.data();
                  application.contentReviewStatus = proofData.status || 'pending';
                  application.brandFeedback = proofData.brandFeedback || '';
                }
              } catch (err) {
                // Silently skip proof status on error
              }
            }
          }
          return application;
        }));
        
        setApplications(enhancedApplications);
      } catch (err) {
        setError('Failed to load applications. Please check your connection and try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [currentUser]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusChange = (value) => {
    setFilters((prev) => ({ ...prev, status: value }));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of table
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const filteredApplications = applications.filter(application => {
    // Filter by search term
    const matchesSearch = 
      application.campaign?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      application.campaign?.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      application.campaign?.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      application.campaign?.brand?.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Filter by status
    const matchesStatus = !filters.status || application.status === filters.status;
    
    return matchesSearch && matchesStatus;
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredApplications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedApplications = filteredApplications.slice(startIndex, endIndex);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, searchTerm]);

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    try {
      // Handle Firestore timestamps
      if (timestamp.toDate) {
        return timestamp.toDate().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      }
      
      // Handle regular dates
      return new Date(timestamp).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (err) {
      return 'Invalid date';
    }
  };

  if (loading) {
    return null;
  }

  if (error) {
    return (
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">My Applications</h2>
          <p className="text-gray-600">Track and manage your campaign applications</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <div className="flex justify-center mb-3">
            <AlertTriangle className="h-10 w-10 text-red-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Something went wrong</h3>
          <p className="text-gray-500 mb-4">{error}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 mb-2">My Applications</h2>
        <p className="text-gray-600">Track and manage your campaign applications</p>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-grow">
              <input
                type="text"
                className="block w-full px-4 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                placeholder="Search by campaign or brand"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
            <div className="flex-shrink-0 md:w-48">
              <Select
                value={filters.status}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>

      {/* Applications List */}
      {paginatedApplications.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Brand
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Applied Date
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                    Next Step
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">View</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedApplications.map((application) => (
                  <React.Fragment key={application.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-100 overflow-hidden">
                          {application.campaign?.brand?.logo ? (
                            <img 
                              src={application.campaign.brand.logo} 
                              alt={application.campaign?.name || 'Campaign'} 
                              className="h-full w-full object-cover"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/assets/default-logo.png';
                              }}
                            />
                          ) : (
                            <div className="h-full w-full bg-primary flex items-center justify-center text-white text-sm">
                              {(application.campaign?.name || 'C').charAt(0).toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {application.campaign?.name || application.campaign?.title || 'Campaign'}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {application.campaign?.description?.substring(0, 60) || 'No description'}
                            {application.campaign?.description?.length > 60 ? '...' : ''}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{application.campaign?.brand?.name || 'Brand'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDate(application.appliedAt)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {(() => {
                          if (application.status === 'pending') {
                            return (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <Clock className="h-3.5 w-3.5 mr-1" />
                                Pending Review
                              </span>
                            );
                          } else if (application.status === 'rejected') {
                            return (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <XCircle className="h-3.5 w-3.5 mr-1" />
                                Rejected
                              </span>
                            );
                          } else if (application.status === 'approved') {
                            if (application.contentReviewStatus === 'approved') {
                              return (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="h-3.5 w-3.5 mr-1" />
                                  Content Approved
                                </span>
                              );
                            } else if (application.contentReviewStatus === 'revision_requested') {
                              return (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                  <XCircle className="h-3.5 w-3.5 mr-1" />
                                  Revision Needed
                                </span>
                              );
                            } else if (application.submissionStatus === 'submitted') {
                              return (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                  <Clock className="h-3.5 w-3.5 mr-1" />
                                  Content Under Review
                                </span>
                              );
                            } else {
                              return (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  <CheckCircle className="h-3.5 w-3.5 mr-1" />
                                  Approved
                                </span>
                              );
                            }
                          }
                          return null;
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm max-w-32">
                        {(() => {
                          if (application.status === 'pending') {
                            return <span className="text-gray-500">Awaiting Approval</span>;
                          } else if (application.status === 'rejected') {
                            return <span className="text-gray-500">Rejected</span>;
                          } else if (application.status === 'approved') {
                            // Pre-approval workflow logic
                            if (application.draftStatus === 'not_submitted') {
                              return (
                                <Link to={`/influencer/draft/submit/${application.campaignId}`} className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium">
                                  Submit Draft <ArrowRight className="h-3 w-3 ml-1" />
                                </Link>
                              );
                            } else if (application.draftStatus === 'pending') {
                              return <span className="text-yellow-600">Draft Under Review</span>;
                            } else if (application.draftStatus === 'rejected' || application.draftStatus === 'revision_requested') {
                              return (
                                <Link to={`/influencer/draft/submit/${application.campaignId}`} className="inline-flex items-center text-orange-600 hover:text-orange-800 font-medium">
                                  Revise Draft <ArrowRight className="h-3 w-3 ml-1" />
                                </Link>
                              );
                            } else if (application.draftStatus === 'approved') {
                              // Draft approved, check proof status
                              if (application.contentReviewStatus === 'approved') {
                                return <span className="text-green-600 font-medium">Completed</span>;
                              } else if (application.contentReviewStatus === 'revision_requested') {
                                return (
                                  <Link to={`/influencer/proof/submit/${application.campaignId}`} className="inline-flex items-center text-orange-600 hover:text-orange-800 font-medium">
                                    Revise Content <ArrowRight className="h-3 w-3 ml-1" />
                                  </Link>
                                );
                              } else if (application.submissionStatus === 'submitted') {
                                return <span className="text-blue-600">Proof Under Review</span>;
                              } else {
                                return (
                                  <Link to={`/influencer/proof/submit/${application.campaignId}`} className="inline-flex items-center text-green-600 hover:text-green-800 font-medium">
                                    Submit Proof <ArrowRight className="h-3 w-3 ml-1" />
                                  </Link>
                                );
                              }
                            } else {
                              // Fallback for unknown draft status
                              return (
                                <Link to={`/influencer/draft/submit/${application.campaignId}`} className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium">
                                  Submit Draft <ArrowRight className="h-3 w-3 ml-1" />
                                </Link>
                              );
                            }
                          }
                          return null;
                        })()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <Link to={`/influencer/campaigns/${application.campaignId}`} className="inline-flex items-center text-primary hover:text-primary-dark">
                        View <ArrowRight className="h-3.5 w-3.5 ml-1" />
                        <span className="sr-only">, {application.campaign?.name}</span>
                      </Link>
                    </td>
                  </tr>
                  {(() => {
                    const hasDraftFeedback = (application.draftStatus === 'rejected' || application.draftStatus === 'revision_requested') && application.draftFeedback;
                    const hasContentFeedback = application.contentReviewStatus === 'revision_requested' && application.brandFeedback;
                    if (!hasDraftFeedback && !hasContentFeedback) return null;
                    const feedback = hasContentFeedback ? application.brandFeedback : application.draftFeedback;
                    return (
                    <tr className="bg-orange-50">
                      <td colSpan="6" className="px-6 py-3">
                        <div className="flex items-start">
                          <AlertTriangle className="h-4 w-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-orange-800 mb-1">Brand Feedback:</p>
                            <p className="text-sm text-orange-700">{feedback}</p>
                            <div className="mt-2 flex gap-3">
                              {application.draftStatus === 'rejected' || application.draftStatus === 'revision_requested' ? (
                                <Link to={`/influencer/draft/submit/${application.campaignId}`} className="inline-flex items-center text-xs font-medium text-orange-700 hover:text-orange-900 underline">
                                  Revise Draft Now <ArrowRight className="h-3 w-3 ml-1" />
                                </Link>
                              ) : application.contentReviewStatus === 'revision_requested' ? (
                                <Link to={`/influencer/proof/submit/${application.campaignId}`} className="inline-flex items-center text-xs font-medium text-orange-700 hover:text-orange-900 underline">
                                  Revise Content Now <ArrowRight className="h-3 w-3 ml-1" />
                                </Link>
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                    );
                  })()}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
              <div className="flex items-center justify-between">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(endIndex, filteredApplications.length)}</span> of{' '}
                      <span className="font-medium">{filteredApplications.length}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Previous</span>
                        <ChevronLeft className="h-5 w-5" />
                      </button>
                      {[...Array(totalPages)].map((_, index) => {
                        const page = index + 1;
                        const isCurrentPage = page === currentPage;
                        const showPage = page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1);
                        
                        if (!showPage) {
                          if (page === currentPage - 2 || page === currentPage + 2) {
                            return (
                              <span key={page} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                                ...
                              </span>
                            );
                          }
                          return null;
                        }
                        
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              isCurrentPage
                                ? 'z-10 bg-primary border-primary text-white'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Next</span>
                        <ChevronRight className="h-5 w-5" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <div className="flex justify-center mb-4">
            <FileText className="h-12 w-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">No applications found</h3>
          <p className="text-gray-500 mb-4">
            {applications.length === 0 
              ? "You haven't applied to any campaigns yet." 
              : "No applications match your current filters."}
          </p>
          {applications.length === 0 ? (
            <Link
              to="/influencer/campaigns"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
            >
              Browse Campaigns
            </Link>
          ) : (
            <button
              onClick={() => {
                setFilters({ status: '' });
                setSearchTerm('');
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark"
            >
              Clear Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default Applications;
